import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../components/ui/AppHeader';
import SettingsHeader from './components/SettingsHeader';
import SettingsTabs from './components/SettingsTabs';
import CalculationDefaultsTab from './components/CalculationDefaultsTab';
import DisplayPreferencesTab from './components/DisplayPreferencesTab';
import ExportOptionsTab from './components/ExportOptionsTab';
import SuccessMessage from './components/SuccessMessage';

const SettingsConfiguration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calculation');
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [settings, setSettings] = useState({
    // Calculation Defaults
    defaultLoanAmount: "250000",
    defaultInterestRate: "6.50",
    defaultPayments: "360",
    defaultPoints: "1.00",
    paymentFrequency: "monthly",
    compoundingMethod: "monthly",
    amortizationType: "normal",
    quickRate1: "5.50",
    quickRate2: "6.00",
    quickRate3: "6.50",
    quickRate4: "7.00",
    
    // Display Preferences
    currencyFormat: "USD",
    decimalPrecision: "2",
    thousandsSeparator: ",",
    dateFormat: "MM/DD/YYYY",
    showPaymentNumbers: true,
    showYearIndicators: true,
    highlightYearlyTotals: true,
    alternatingRowColors: true,
    showRunningBalance: true,
    showCumulativeInterest: true,
    showPaymentChart: true,
    showBalanceChart: true,
    
    // Export Options
    defaultExportFormat: "pdf",
    pageOrientation: "portrait",
    pageSize: "letter",
    includeLoanSummary: true,
    includePaymentSchedule: true,
    includeCharts: true,
    includeYearlyTotals: true,
    includeCalculationNotes: false,
    includeDisclaimer: true,
    reportHeaderText: "Loan Amortization Schedule",
    reportFooterText: "Generated by AmortizationPro",
    companyLogo: "",
    disclaimerText: "This calculation is for informational purposes only and should not be considered as financial advice."
  });

  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    // Load settings from localStorage on component mount
    const savedSettings = localStorage.getItem('amortizationProSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(prev => ({ ...prev, ...parsed }));
      setOriginalSettings({ ...settings, ...parsed });
    } else {
      setOriginalSettings({ ...settings });
    }
  }, []);

  useEffect(() => {
    // Check if settings have changed
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    try {
      localStorage.setItem('amortizationProSettings', JSON.stringify(settings));
      setOriginalSettings({ ...settings });
      setHasChanges(false);
      setSuccessMessage('Settings saved successfully!');
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSuccessMessage('Failed to save settings. Please try again.');
      setShowSuccess(true);
    }
  };

  const handleReset = () => {
    const defaultSettings = {
      defaultLoanAmount: "250000",
      defaultInterestRate: "6.50",
      defaultPayments: "360",
      defaultPoints: "1.00",
      paymentFrequency: "monthly",
      compoundingMethod: "monthly",
      amortizationType: "normal",
      quickRate1: "5.50",
      quickRate2: "6.00",
      quickRate3: "6.50",
      quickRate4: "7.00",
      currencyFormat: "USD",
      decimalPrecision: "2",
      thousandsSeparator: ",",
      dateFormat: "MM/DD/YYYY",
      showPaymentNumbers: true,
      showYearIndicators: true,
      highlightYearlyTotals: true,
      alternatingRowColors: true,
      showRunningBalance: true,
      showCumulativeInterest: true,
      showPaymentChart: true,
      showBalanceChart: true,
      defaultExportFormat: "pdf",
      pageOrientation: "portrait",
      pageSize: "letter",
      includeLoanSummary: true,
      includePaymentSchedule: true,
      includeCharts: true,
      includeYearlyTotals: true,
      includeCalculationNotes: false,
      includeDisclaimer: true,
      reportHeaderText: "Loan Amortization Schedule",
      reportFooterText: "Generated by AmortizationPro",
      companyLogo: "",
      disclaimerText: "This calculation is for informational purposes only and should not be considered as financial advice."
    };
    
    setSettings(defaultSettings);
    localStorage.removeItem('amortizationProSettings');
    setSuccessMessage('Settings reset to defaults!');
    setShowSuccess(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calculation':
        return (
          <CalculationDefaultsTab
            settings={settings}
            onSettingChange={handleSettingChange}
          />
        );
      case 'display':
        return (
          <DisplayPreferencesTab
            settings={settings}
            onSettingChange={handleSettingChange}
          />
        );
      case 'export':
        return (
          <ExportOptionsTab
            settings={settings}
            onSettingChange={handleSettingChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      
      <div className="pt-15">
        <SettingsHeader
          onSave={handleSave}
          onReset={handleReset}
          hasChanges={hasChanges}
        />
        
        <SettingsTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="bg-card border border-border rounded-lg shadow-soft">
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </main>
      </div>
      
      <SuccessMessage
        message={successMessage}
        isVisible={showSuccess}
        onHide={() => setShowSuccess(false)}
      />
    </div>
  );
};

export default SettingsConfiguration;